import { DomHighlightsRenderer, type HighlightsRenderer } from './highlights-renderer.ts';
import { type GeometryResolver } from './types.ts';

/**
 * Controller-owned resources supplied to one rendering strategy instance.
 */
export interface HighlightsRenderingStrategyContext {
  readonly container: HTMLElement;
  readonly resolver: GeometryResolver;
  invalidateGeometry(): void;
}

/**
 * Controller-level policy for creating logical layers.
 *
 * A strategy may share physical surfaces or layout observation across every
 * layer it creates. Feature code never resolves or calls a strategy directly;
 * application bootstrap selects one and `HighlightsController` owns its
 * lifecycle.
 */
export interface HighlightsRenderingStrategy {
  createLayer(): HighlightsRenderer;
  dispose(): void;
}

export type CreateHighlightsRenderingStrategy = (
  context: HighlightsRenderingStrategyContext
) => HighlightsRenderingStrategy;

/**
 * Default strategy for layouts where every highlight uses one DOM coordinate
 * space. Each logical layer receives one `DomHighlightsRenderer`.
 */
export function createDomHighlightsRenderingStrategy({
  container,
  resolver
}: HighlightsRenderingStrategyContext): HighlightsRenderingStrategy {
  const layers = new Set<DomHighlightsRenderer>();

  return {
    createLayer() {
      const layer = new DomHighlightsRenderer(container, resolver, () => {
        layers.delete(layer);
      });

      layers.add(layer);

      return layer;
    },

    dispose() {
      for (const layer of [...layers]) {
        layer.dispose();
      }

      layers.clear();
    }
  };
}
